"use client";

import { cancelFrame, frame } from "framer-motion";
import { ReactLenis } from "lenis/react";
import type { LenisRef } from "lenis/react";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";

type Props = { children: ReactNode };

function getLenisInstance(ref: React.RefObject<LenisRef | null>) {
    const r = ref.current;
    return r?.lenis ?? null;
}

export default function SmoothScroll({ children }: Props) {
    const pathname = usePathname();
    const lenisRef = useRef<LenisRef>(null);

    const isStudio = pathname?.startsWith("/studio");
    const isFamiliada = pathname?.startsWith("/familiada");

    const [isTouchDevice, setIsTouchDevice] = useState<boolean | null>(null);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

    useEffect(() => {
        if (typeof window === "undefined") return;

        const mql = window.matchMedia("(pointer: coarse)");
        const motionMql = window.matchMedia("(prefers-reduced-motion: reduce)");

        const checkTouch = () => setIsTouchDevice(Boolean(mql.matches));
        const checkMotion = () => setPrefersReducedMotion(Boolean(motionMql.matches));

        checkTouch();
        checkMotion();

        mql.addEventListener?.("change", checkTouch);
        motionMql.addEventListener?.("change", checkMotion);

        return () => {
            mql.removeEventListener?.("change", checkTouch);
            motionMql.removeEventListener?.("change", checkMotion);
        };
    }, []);

    const enableLenis = useMemo(() => {
        if (isStudio) return false;
        if (isFamiliada) return false;
        if (prefersReducedMotion) return false;
        if (isTouchDevice === null) return false;
        return isTouchDevice === false;
    }, [isStudio, isFamiliada, prefersReducedMotion, isTouchDevice]);

    // Oficjalna integracja Lenis + Framer Motion (wspólny frame loop)
    useEffect(() => {
        if (!enableLenis) return;

        function update(data: { timestamp: number }) {
            lenisRef.current?.lenis?.raf(data.timestamp);
        }

        frame.update(update, true);
        return () => cancelFrame(update);
    }, [enableLenis]);

    useEffect(() => {
        if (!enableLenis) return;
        if ("scrollRestoration" in window.history) {
            window.history.scrollRestoration = "manual";
        }
    }, [enableLenis]);

    useEffect(() => {
        if (isStudio) return;

        const scrollToTop = () => {
            if (!enableLenis) {
                window.scrollTo(0, 0);
                return;
            }
            const lenis = getLenisInstance(lenisRef);
            if (lenis && typeof lenis.scrollTo === "function") {
                lenis.scrollTo(0, { immediate: true });
            } else {
                window.scrollTo(0, 0);
            }
        };

        const id1 = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                scrollToTop();
                setTimeout(() => {
                    scrollToTop();
                    window.dispatchEvent(new Event("scroll"));
                }, 100);
            });
        });

        return () => cancelAnimationFrame(id1);
    }, [pathname, isStudio, enableLenis]);

    if (!enableLenis) {
        return <div key={pathname ?? "root"}>{children}</div>;
    }

    return (
        <ReactLenis
            ref={lenisRef}
            root
            options={{
                autoRaf: false,
                // Krótszy duration = mniej „drgania” po zwalnieniu kółka
                duration: 0.9,
                easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
                orientation: "vertical",
                gestureOrientation: "vertical",
                smoothWheel: true,
                wheelMultiplier: 0.9,
                touchMultiplier: 1,
                syncTouch: false,
            }}
        >
            <div key={pathname ?? "root"}>{children}</div>
        </ReactLenis>
    );
}
